import PasswordInput from './PasswordInput';
import { useState, useEffect } from 'react';
import logoMark from '../assets/logo-gestion360.png';
import { BRAND, applyBrandTheme } from '../config/brand';
import { getMyOrg } from '../lib/org';
import { signIn, getProfile, sendPasswordReset, updatePassword, supabase, orgLoginEmail } from '../lib/supabase';

// mode: 'org' (/o/<enlace>: administradores y empleados de esa organización) | 'platform' (/plataforma) | 'portal' (/ingresar: buscar la organización)
export default function LoginScreen({ onLogin, brand, mode = 'org', forceReset = false, onResetDone }) {
  const [email, setEmail] = useState('');
  useEffect(() => { if (mode === 'org' && brand?.brand_color) applyBrandTheme(brand.brand_color); }, [mode, brand?.brand_color]);
  const hideProduct = mode === 'org' && !!brand?.hide_product_brand;
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(forceReset ? 'reset' : 'login'); // 'login' | 'forgot' | 'reset'
  const [forgotEmail, setForgotEmail] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [newPwConfirm, setNewPwConfirm] = useState('');
  const [resetDone, setResetDone] = useState(false);

  // Detect password reset link
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    if (search.includes('reset=1') || hash.includes('type=recovery')) {
      setView('reset');
    }
    // Supabase sends #access_token= on password reset
    if (hash.includes('access_token') && hash.includes('type=recovery')) {
      setView('reset');
    }
  }, []);

  const doLogin = async () => {
    setLoading(true); setErr('');
    try {
      if (mode === 'org' && !brand?.id) throw new Error(brand?.missing ? 'Esta organización no existe o no está activa.' : 'Esperá un instante a que cargue la organización e intentá de nuevo.');
      const loginEmail = mode === 'org' ? await orgLoginEmail(email, brand?.id) : email;
      const { user } = await signIn(loginEmail, pw);
      if (mode === 'platform') {
        const o = await getMyOrg().catch(() => null);
        if (!o?.platform_admin) {
          await supabase.auth.signOut();
          throw new Error('Esta dirección es solo para la administración de Gestión360. Ingresá desde la dirección de tu organización.');
        }
        onLogin(null);
        return;
      }
      const profile = await getProfile(user.id);
      if (profile?.organization_id !== brand.id) {
        await supabase.auth.signOut();
        throw new Error(`Esta cuenta no pertenece a ${brand.name}. Ingresá desde la dirección de tu organización.`);
      }
      onLogin(profile);
    } catch (e) {
      setErr(e.message === 'Invalid login credentials' ? 'Email o contraseña incorrectos.' : e.message);
    }
    setLoading(false);
  };

  const doForgot = async () => {
    if (!forgotEmail) return setErr('Ingresá tu email');
    setLoading(true); setErr('');
    try {
      await sendPasswordReset(mode === 'org' ? await orgLoginEmail(forgotEmail, brand?.id) : forgotEmail, mode === 'platform' ? '/plataforma' : (brand?.slug ? `/o/${brand.slug}` : '/'));
      setForgotSent(true);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const doReset = async () => {
    if (!newPw || newPw.length < 6) return setErr('La contraseña debe tener al menos 6 caracteres');
    if (newPw !== newPwConfirm) return setErr('Las contraseñas no coinciden');
    setLoading(true); setErr('');
    try {
      await updatePassword(newPw);
      setResetDone(true);
      setTimeout(() => { setView('login'); window.history.replaceState({}, '', '/'); onResetDone?.(); }, 3000);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#0f172a 100%)' }}>
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle,#5667a8,transparent)' }} />
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle,#818cf8,transparent)' }} />

      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={(mode === 'org' && !brand?.missing && brand?.logo_url) || logoMark} alt={BRAND.product} className="w-16 h-16 rounded-3xl mx-auto mb-3 shadow-2xl object-contain bg-white/5"/>
          <h1 className="text-white text-2xl font-bold tracking-tight mb-1">{mode === 'org' && !brand?.missing && brand?.name || BRAND.product}</h1>
          <p className="text-slate-400 text-sm">{mode === 'platform' ? 'Administración de la plataforma' : mode === 'portal' ? BRAND.tagline : brand && !brand.missing ? (hideProduct ? 'Ingreso' : `Ingreso · ${BRAND.product}`) : BRAND.tagline}</p>
          {mode === 'org' && brand?.missing && <p className="mt-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">No encontramos esa organización o no está activa. Revisá el enlace que te enviaron.</p>}
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* LOGIN */}
          {view === 'login' && mode === 'portal' && (
            <div className="p-6 space-y-4">
              <h2 className="text-base font-bold text-gray-800">Ingresá a tu organización</h2>
              <p className="text-xs text-gray-500">Cada organización tiene su propia dirección de ingreso, la misma para sus administradores y empleados. Escribí el código que te dieron (por ejemplo, <b>ellazo</b>).</p>
              <div className="flex gap-2">
                <input value={orgCode} onChange={e => setOrgCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  onKeyDown={e => e.key === 'Enter' && orgCode.length > 1 && (window.location.href = `/o/${orgCode}`)}
                  placeholder="Código de tu organización"
                  className="flex-1 px-3.5 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50" />
                <button type="button" disabled={orgCode.length < 2} onClick={() => { window.location.href = `/o/${orgCode}`; }}
                  className="px-5 rounded-2xl text-sm font-bold text-white disabled:opacity-40" style={{ background: 'var(--brand-grad)' }}>Ir</button>
              </div>
              <a href="/" className="block text-center text-xs text-gray-400 hover:text-gray-600 py-1">¿Querés usar {BRAND.product} en tu organización? Ver planes</a>
            </div>
          )}
          {view === 'login' && mode !== 'portal' && (
            <>
              <div className="px-6 pt-6 pb-2 border-b border-gray-100">
                <h2 className="text-base font-bold text-gray-800">Iniciar sesión</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && doLogin()}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Contraseña</label>
                  <PasswordInput value={pw} onChange={e => setPw(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && doLogin()}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
                </div>
                {err && <p className="text-red-500 text-xs bg-red-50 px-3 py-2.5 rounded-xl">{err}</p>}
                <button onClick={doLogin} disabled={loading}
                  className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-60"
                  style={{ background: 'var(--brand-grad)' }}>
                  {loading ? 'Ingresando...' : 'Entrar'}
                </button>
                <button onClick={() => { setView('forgot'); setErr(''); }}
                  className="w-full text-xs text-sky-500 hover:text-sky-700 transition-colors py-1">
                  ¿Olvidaste tu contraseña?
                </button>
                {mode === 'org' && !hideProduct && <a href="/" className="block text-center text-xs text-gray-400 hover:text-gray-600 py-1">Conocé {BRAND.product}</a>}
              </div>
            </>
          )}

          {/* FORGOT PASSWORD */}
          {view === 'forgot' && (
            <>
              <div className="px-6 pt-6 pb-2 border-b border-gray-100">
                <button onClick={() => { setView('login'); setErr(''); setForgotSent(false); }}
                  className="text-xs text-gray-400 hover:text-gray-600 mb-2 flex items-center gap-1">
                  ← Volver
                </button>
                <h2 className="text-base font-bold text-gray-800">Recuperar contraseña</h2>
              </div>
              <div className="p-6 space-y-4">
                {!forgotSent ? <>
                  <p className="text-xs text-gray-500">Te enviamos un link a tu email para crear una contraseña nueva.</p>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Tu email</label>
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && doForgot()}
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
                  </div>
                  {err && <p className="text-red-500 text-xs bg-red-50 px-3 py-2.5 rounded-xl">{err}</p>}
                  <button onClick={doForgot} disabled={loading}
                    className="w-full py-3.5 rounded-2xl text-sm font-bold text-white disabled:opacity-60"
                    style={{ background: 'var(--brand-grad)' }}>
                    {loading ? 'Enviando...' : 'Enviar link de recuperación'}
                  </button>
                </> : (
                  <div className="text-center py-4 space-y-3">
                    <div className="text-5xl">📧</div>
                    <p className="font-bold text-gray-900">¡Email enviado!</p>
                    <p className="text-sm text-gray-500">Revisá tu casilla <span className="font-semibold text-gray-700">{forgotEmail}</span> y hacé clic en el link para crear tu nueva contraseña.</p>
                    <button onClick={() => { setView('login'); setForgotSent(false); setForgotEmail(''); }}
                      className="text-xs text-sky-500 hover:text-sky-700 mt-2">Volver al inicio</button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* RESET PASSWORD (from email link) */}
          {view === 'reset' && (
            <>
              <div className="px-6 pt-6 pb-2 border-b border-gray-100">
                <h2 className="text-base font-bold text-gray-800">Nueva contraseña</h2>
              </div>
              <div className="p-6 space-y-4">
                {!resetDone ? <>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Nueva contraseña</label>
                    <PasswordInput value={newPw} onChange={e => setNewPw(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Confirmar contraseña</label>
                    <PasswordInput value={newPwConfirm} onChange={e => setNewPwConfirm(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && doReset()}
                      placeholder="Repetí la contraseña"
                      className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
                  </div>
                  {err && <p className="text-red-500 text-xs bg-red-50 px-3 py-2.5 rounded-xl">{err}</p>}
                  <button onClick={doReset} disabled={loading}
                    className="w-full py-3.5 rounded-2xl text-sm font-bold text-white disabled:opacity-60"
                    style={{ background: 'var(--brand-grad)' }}>
                    {loading ? 'Guardando...' : 'Guardar nueva contraseña'}
                  </button>
                </> : (
                  <div className="text-center py-4 space-y-3">
                    <div className="text-5xl">✅</div>
                    <p className="font-bold text-gray-900">¡Contraseña actualizada!</p>
                    <p className="text-sm text-gray-500">Redirigiendo al inicio...</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <p className="text-center text-[11px] text-slate-500 mt-4">
          <a href="/terminos" className="hover:text-slate-300">Términos</a> · <a href="/privacidad" className="hover:text-slate-300">Privacidad</a>
        </p>
      </div>
    </div>
  );
}
